import { useState, useEffect } from 'react';

/**
 * Checks if the user is authenticated by reading directly from localStorage
 * This ensures isAuthenticated can never be out of sync with actual token state
 * 
 * @returns {boolean} True if user is authenticated, false otherwise
 */
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		// Auth0 SDK stores cache with keys like @@auth0spajs@@::...
		const auth0CacheKeys = Object.keys(localStorage).filter(key => 
			key.includes('@@auth0spajs@@')
		);

		if (auth0CacheKeys.length === 0) {
			// No Auth0 cache entries - user is not authenticated
			return false;
		}

		// Check if any cache entry contains valid authentication data
		for (const key of auth0CacheKeys) {
			try {
				const cacheValue = localStorage.getItem(key);
				if (cacheValue) {
					const cacheData = JSON.parse(cacheValue);
					// Check if cache contains authentication state
					// Auth0 SDK stores isAuthenticated in the cache body
					if (cacheData?.body?.isAuthenticated === true) {
						return true;
					}
					// Also check for access token or user data as fallback
					if (cacheData?.body?.access_token || cacheData?.body?.user) {
						return true;
					}
				}
			} catch (e) {
				// Skip invalid cache entries
				continue;
			}
		}

		// If we have cache keys but no valid auth data, user is not authenticated
		return false;
	} catch (error) {
		// If we can't check, assume not authenticated to be safe
		console.warn('⚠️  Could not check Auth0 authentication state:', error);
		return false;
	}
};

/**
 * Hook that reads isAuthenticated directly from localStorage
 * This ensures it can never be out of sync with actual token state
 * 
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const useIsAuthenticated = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(() => isUserAuthenticated());

	useEffect(() => {
		// Check immediately on mount
		setIsAuthenticated(isUserAuthenticated());

		// Listen for storage changes (logout in other tabs, localStorage.clear(), etc.)
		const handleStorageChange = (e) => {
			// Check if Auth0 cache was modified
			if (e.key && e.key.includes('@@auth0spajs@@')) {
				setIsAuthenticated(isUserAuthenticated());
			}
			// Also check if localStorage was cleared (logout)
			if (e.key === null) {
				// localStorage.clear() was called - check all keys
				setIsAuthenticated(isUserAuthenticated());
			}
		};

		// Listen for custom logout event
		const handleLogoutInitiated = () => {
			setIsAuthenticated(isUserAuthenticated());
		};

		window.addEventListener('storage', handleStorageChange);
		window.addEventListener('logout-initiated', handleLogoutInitiated);

		// Poll periodically to catch changes that don't trigger storage events
		// (e.g., same-tab localStorage changes)
		const interval = setInterval(() => {
			const currentAuth = isUserAuthenticated();
			if (currentAuth !== isAuthenticated) {
				setIsAuthenticated(currentAuth);
			}
		}, 500); // Check every 500ms

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('logout-initiated', handleLogoutInitiated);
			clearInterval(interval);
		};
	}, [isAuthenticated]);

	return isAuthenticated;
};
